import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { CardContainer } from "../../../components/Card/CardContainer";
import { useForm } from "antd/es/form/Form";
import PageTitleNested from "../../../components/PageTitle/PageTitleNested";
import BreadCrumb from "../../../components/BreadCrumb/BreadCrumb";
import { Card, Col, ColorPicker, Divider, Flex, Form, Row, Switch, Tag, Typography } from "antd";
import Input from "../../../components/Input/Input";
import TextArea from "../../../components/Input/TextArea";
import Button from "../../../components/Button/Button";
import { CheckOutlined } from "@ant-design/icons";
import {
  checkCodeColor,
  checkCodeCOType,
  createCoDiscoutType,
  getCoDiscoutTypeById,
  updateCoDiscoutType,
} from "../../../datasource/CODiscountDatasource";
import { useMessageContext } from "../../../context/MessageProvider";
import Text from "../../../components/Text/Text";

interface ColorWithHexString {
  toHexString: () => string;
}

const getHexColor = (input: unknown): string => {
  if (!input) {
    return "";
  }
  if (typeof input === "string") {
    return input;
  }
  if (
    typeof input === "object" &&
    "toHexString" in input &&
    typeof (input as ColorWithHexString).toHexString === "function"
  ) {
    return (input as ColorWithHexString).toHexString();
  }
  return "";
};

export const CreateCODiscountType: React.FC = () => {
  const { messageApi } = useMessageContext();
  const profile = JSON.parse(localStorage.getItem("profile")!);
  const { pathname } = window.location;
  const pathSplit = pathname.split("/") as Array<string>;
  const navigate = useNavigate();
  const isCreate = pathSplit[pathSplit.length - 1] === "0";
  const id = pathSplit[pathSplit.length - 1];
  const [form] = useForm();
  const _watchColor = Form.useWatch("color", form);
  const _watchCode = Form.useWatch("code", form);
  const _watchNameTh = Form.useWatch("nameTh", form);
  const watchColorHex = getHexColor(_watchColor);

  const fetchData = async () => {
    await getCoDiscoutTypeById(id).then((res) => {
      form.setFieldsValue({
        creditMemoTypeId: res.creditMemoTypeId,
        nameTh: res.nameTh,
        nameEn: res.nameEn,
        color: res.color,
        code: res.code,
        description: res.description,
        status: res.status,
        coCount: res.coCount,
      });
    });
  };

  useEffect(() => {
    if (id !== "0") {
      fetchData();
    }
  }, [id]);

  const PageTitle = () => {
    return (
      <PageTitleNested
        title={isCreate ? "สร้างประเภทส่วนลด" : "แก้ไขประเภทส่วนลด"}
        showBack
        customBreadCrumb={
          <BreadCrumb
            data={[
              { text: "ประเภทส่วนลด", path: "/newDiscountCo/coDiscountType" },
              {
                text: isCreate ? "สร้างประเภทส่วนลด" : "แก้ไขประเภทส่วนลด",
                path: window.location.pathname,
              },
            ]}
          />
        }
        //onBack={() => setConfirmExitModal(true)}
      />
    );
  };

  const onSubmit = async () => {
    try {
      await form.validateFields();
      const payload = {
        ...form.getFieldsValue(true),
        color: watchColorHex || undefined,
      };
      payload.company = profile?.company;
      payload.isDeleted = false;
      isCreate && (payload.createBy = profile?.firstname + " " + profile?.lastname);
      payload.updateBy = profile?.firstname + " " + profile?.lastname;

      if (isCreate) {
        await createCoDiscoutType(payload).then((res) => {
          if (res?.success) {
            navigate("/newDiscountCo/coDiscountType");
            messageApi.success("บันทึกสำเร็จ");
          }
        });
      } else {
        await updateCoDiscoutType(payload).then((res) => {
          if (res?.success) {
            navigate("/newDiscountCo/coDiscountType");
            messageApi.success("แก้ไขสำเร็จ");
          }
        });
      }
    } catch (error) {
      console.log("error", error);
    }
  };

  const checkColorUsed = async (value: any) => {
    const hex = value?.toHexString();
    const hsb = value?.toHsb();
    const isDupColor = await checkCodeColor({
      company: profile?.company,
      color: hex,
      excludeId: id == "0" ? undefined : id,
    }).then((res) => {
      return res.isDuplicate;
    });

    if (isDupColor) {
      return {
        message: "สีนี้ถูกใช้งานแล้ว",
        success: true,
      };
    }
    const calHsb = [
      parseFloat((hsb.h * 360).toFixed(0)),
      parseFloat((hsb.s * 100).toFixed(0)),
      parseFloat((hsb.b * 100).toFixed(0)),
      parseFloat((hsb.a * 100).toFixed(0)),
    ];
    if (calHsb[1] < 40 || calHsb[2] > 80) {
      return {
        message: "สีกลืนกับพื้นหลัง",
        success: true,
      };
    }

    return {
      message: "",
      success: false,
    };
  };

  return (
    <>
      <CardContainer>
        <PageTitle />
        <Divider size='small' />
        <br />
        <div>
          <span
            className='card-label font-weight-bolder text-dark'
            style={{ fontSize: 20, fontWeight: "bold" }}
          >
            รายละเอียดข้อมูลส่วนลด
          </span>
        </div>
        <Form form={form} layout='vertical'>
          <Row justify={"space-around"} gutter={8}>
            <Col span={12}>
              <Form.Item
                label='ชื่อประเภทภาษาไทย'
                name='nameTh'
                rules={[{ required: true, message: "โปรดระบุชื่อประเภทภาษาไทย" }]}
                required
              >
                <Input
                  placeholder='ระบุชื่อประเภทภาษาไทย'
                  autoComplete='off'
                  maxLength={30}
                  showCount
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label='ชื่อประเภทภาษาอังกฤษ'
                name='nameEn'
                //rules={[{ required: true, message: "โปรดระบุชื่อประเภทภาษาอังกฤษ" }]}
              >
                <Input
                  placeholder='ระบุชื่อประเภทภาษาอังกฤษ'
                  autoComplete='off'
                  maxLength={30}
                  showCount
                />
              </Form.Item>
            </Col>
          </Row>
          <Row justify={"start"} gutter={8}>
            <Col span={3}>
              <Form.Item
                label='สี'
                name='color'
                rules={[
                  { required: true, message: "โปรดระบุสีประเภท" },
                  {
                    validator: async (_, value) => {
                      if (!value || /^#[0-9A-Fa-f]{6}$/.test(value)) {
                        return Promise.resolve();
                      } else {
                        const isColor = await checkColorUsed(value);
                        if (isColor.success) {
                          return Promise.reject(isColor.message);
                        }
                      }
                    },
                  },
                ]}
                required
              >
                <ColorPicker
                  size='large'
                  defaultFormat={"hex"}
                  showText
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
            <Col span={9}>
              <Form.Item
                label='รหัส'
                name='code'
                rules={[
                  { required: true, message: "โปรดระบุรหัส" },
                  {
                    validator: async (_, value) => {
                      if (value) {
                        const formattedValue = value?.toUpperCase()?.replace(/[^A-Z0-9]/g, "");
                        const isDup = await checkCodeCOType({
                          company: profile?.company,
                          code: formattedValue,
                          excludeId: id == "0" ? undefined : id,
                        }).then((res) => {
                          return res.isDuplicate;
                        });
                        if (isDup) {
                          return Promise.reject(
                            "รหัสนี้ถูกใช้งานแล้ว กรุณาตรวจสอบหรือเปลี่ยนรหัสใหม่",
                          );
                        }
                        return Promise.resolve();
                      }
                    },
                  },
                ]}
                required
              >
                <Input
                  placeholder='ระบุรหัส'
                  autoComplete='off'
                  maxLength={3}
                  showCount
                  onChange={(e) => {
                    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "");
                    form.setFieldValue("code", value);
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <div>ตัวอย่างการแสดงผล</div>
              <Tag
                style={{
                  backgroundColor: watchColorHex ? `${watchColorHex}20` : "#F3F6F9",
                  border: "none",
                }}
              >
                <Typography.Text
                  style={{
                    fontFamily: "IBM Plex Sans Thai",
                    color: watchColorHex || "#262626",
                    fontWeight: "bold",
                    fontSize: 16,
                  }}
                >
                  {form.getFieldValue("code") ? form.getFieldValue("code") : "รหัส"} •{" "}
                  {form.getFieldValue("nameTh")
                    ? form.getFieldValue("nameTh")
                    : "ชื่อประเภทภาษาไทย"}{" "}
                </Typography.Text>
              </Tag>
            </Col>
          </Row>
          <Row justify={"space-around"} gutter={8}>
            <Col span={24}>
              <Form.Item label='รายละเอียด' name='description'>
                <TextArea rows={3} placeholder='ระบุรายละเอียด' autoComplete='off' />
              </Form.Item>
            </Col>
          </Row>
          <Card style={{ backgroundColor: "#FAFAFA" }}>
            <Row gutter={16} justify={"start"}>
              <Col span={1}>
                <Form.Item name='status' valuePropName='checked' initialValue={true}>
                  <Switch disabled={form.getFieldValue("coCount") > 0} />
                </Form.Item>
              </Col>
              <Col span={15}>
                <Text>เปิดใช้งานประเภทส่วนลด</Text>
                <br />
                <Text color='Text3'>
                  เมื่อเปิดใช้งานและนำประเภทนี้ไปสร้างรายการส่วนลดแล้ว จะไม่สามารถปิดใช้งานหรือลบได้
                  หากต้องการปิดหรือลบในภายหลัง กรุณานำประเภทนี้ออกจากรายการส่วนลดที่เกี่ยวข้องก่อน
                </Text>
              </Col>
            </Row>
          </Card>
        </Form>
        <Divider />
        <Row justify={"end"}>
          <Col span={3}>
            <Button type='primary' title='บันทึก' height={40} size='large' onClick={onSubmit} />
          </Col>
        </Row>
      </CardContainer>
    </>
  );
};

export const DatePickerCustom: React.FC<{ value?: string; onChange?: (value: string) => void }> = ({
  value,
  onChange,
}) => {
  const colors = [
    "#D93F92",
    "#6C34C8",
    "#E53935",
    "#E9642D",
    "#EA9533",
    "#EDB03A",
    "#A8D23B",
    "#58B9BC",
    "#4A89E8",
    "#3E57D8",
  ];

  return (
    <Input
      readOnly
      // value={value}
      style={{ height: "auto", padding: "12px 16px" }}
      prefix={
        <Flex gap={10}>
          {colors.map((item) => (
            <div
              key={item}
              onClick={() => onChange?.(item)}
              style={{
                width: 22,
                height: 22,
                borderRadius: "50%",
                backgroundColor: item,
                cursor: "pointer",
                border: "2px solid #fff",
                boxShadow: value === item ? `0 0 0 2px ${item}` : "none",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                transition: "all .2s",
              }}
            >
              {value === item && (
                <CheckOutlined
                  style={{
                    color: "#fff",
                    fontSize: 12,
                    fontWeight: "bold",
                  }}
                />
              )}
            </div>
          ))}
        </Flex>
      }
    />
  );
};
